/** @format */

import { ExclamationCircleOutlined, LockOutlined, RightOutlined } from "@ant-design/icons";
import { Button, Tooltip } from "antd";
import moment from "moment";
import React from "react";
import {
  TGiornoSettimana,
  TGruppoSettimana,
  TPersonaSettimana,
  TRepartoSettimana,
} from "../../../../redux-modules/declarations/maps/turniSettimana";
import { TFiltroPersone, TUnita } from "../format";
import { Griglia } from "../styles";
import { BloccoTotali } from "./BloccoTotali";
import { RigaPersona, TApriAssegnazione, TApriTurno } from "./RigaPersona";

export interface TRepartoVista {
  reparto: TRepartoSettimana;
  persone: TPersonaSettimana[];
}

export interface TGruppoVista {
  chiave: string;
  gruppo: TGruppoSettimana;
  aperto: boolean;
  personeVisibili: number;
  reparti: TRepartoVista[];
}

interface Props {
  giorni: TGiornoSettimana[];
  gruppi: TGruppoVista[];
  oggi: string;
  unita: TUnita;
  filtrando: boolean;
  puoCreare: boolean;
  /** turni anche nei giorni precedenti a oggi (ShiftPlanning/Cancreatepastdays) */
  puoCrearePassato: boolean;
  /** vista pulita: niente avvisi e anomalie, solo la pianificazione */
  pulita: boolean;
  onScroll: () => void;
  onApriChiudiGruppo: (chiave: string) => void;
  onFiltro: (filtro: TFiltroPersone) => void;
  onAzzeraFiltri: () => void;
  onApriAssegnazione: TApriAssegnazione;
  onApriTurno: TApriTurno;
}

const plurale = (n: number, uno: string, molti: string): string => `${n} ${n === 1 ? uno : molti}`;

const TestataGiorno = ({
  giorno,
  oggi,
}: {
  giorno: TGiornoSettimana;
  oggi: string;
}): JSX.Element => {
  const m = moment(giorno.data);
  const feste = giorno.festivita
    .map(f => f.descrizione)
    .filter((d, i, tutte) => tutte.indexOf(d) === i);

  return (
    <div className={`giorno${giorno.data === oggi ? " e-oggi" : ""}`}>
      <span className="settimanale">{m.format("ddd")}</span>
      <span className="numero">
        {m.format("D")}
        {giorno.data === oggi && <span className="oggi">Oggi</span>}
      </span>
      {giorno.bloccato && (
        <span className="bloccato">
          <LockOutlined /> Bloccato
        </span>
      )}
      {/* una festività e un post-it per riga: il testo lungo si tronca, intero nel tooltip */}
      {feste.map(f => (
        <Tooltip key={`festa-${f}`} title={f}>
          <span className="nota festa">{f}</span>
        </Tooltip>
      ))}
      {giorno.annotazioni.map((a, i) => (
        <Tooltip key={`nota-${i}`} title={a}>
          <span className="nota">{a}</span>
        </Tooltip>
      ))}
    </div>
  );
};

export const GrigliaSettimana = ({
  giorni,
  gruppi,
  oggi,
  unita,
  filtrando,
  puoCreare,
  puoCrearePassato,
  pulita,
  onScroll,
  onApriChiudiGruppo,
  onFiltro,
  onAzzeraFiltri,
  onApriAssegnazione,
  onApriTurno,
}: Props): JSX.Element => {
  const nessuno = gruppi.every(g => g.personeVisibili === 0);

  return (
    <Griglia onScroll={onScroll}>
      <div className="contenuto">
        <div className="riga testata">
          <div className="col-nome">Persona e contratto</div>
          {giorni.map(g => (
            <TestataGiorno key={g.data} giorno={g} oggi={oggi} />
          ))}
          <div className="col-totali">
            Settimana
            <span className="unita">assegnate di previste, in {unita}</span>
          </div>
        </div>

        {nessuno && (
          <div className="vuoto">
            <p style={{ fontWeight: 700, marginBottom: 10 }}>
              {filtrando
                ? "Nessuna persona corrisponde ai filtri."
                : "Nessuna persona da mostrare in questa settimana."}
            </p>
            {filtrando && <Button onClick={onAzzeraFiltri}>Mostra tutti</Button>}
          </div>
        )}

        {gruppi
          .filter(g => g.personeVisibili > 0)
          .map(({ chiave, gruppo, aperto, personeVisibili, reparti }) => (
            <React.Fragment key={chiave}>
              <div className="riga gruppo">
                <div className="col-nome">
                  <button
                    type="button"
                    className="apri-gruppo"
                    aria-expanded={aperto}
                    onClick={(): void => onApriChiudiGruppo(chiave)}
                  >
                    <RightOutlined className={`freccia${aperto ? " aperta" : ""}`} />
                    {gruppo.nome}
                  </button>
                  <span className="conta-persone">
                    {personeVisibili === gruppo.totali.persone
                      ? plurale(gruppo.totali.persone, "persona", "persone")
                      : `${personeVisibili} di ${plurale(
                          gruppo.totali.persone,
                          "persona",
                          "persone",
                        )}`}
                  </span>
                </div>
                <div className="centro-gruppo">
                  {!pulita && gruppo.totali.mancate_timbrature > 0 && (
                    <button
                      type="button"
                      className="segnale rosso"
                      onClick={(): void => onFiltro("mancate")}
                    >
                      <ExclamationCircleOutlined />
                      {plurale(
                        gruppo.totali.mancate_timbrature,
                        "mancata timbratura",
                        "mancate timbrature",
                      )}
                    </button>
                  )}
                  {!pulita && gruppo.totali.persone_con_avvisi > 0 && (
                    <button
                      type="button"
                      className="segnale ambra"
                      onClick={(): void => onFiltro("avvisi")}
                    >
                      {plurale(
                        gruppo.totali.persone_con_avvisi,
                        "persona con avvisi di contratto",
                        "persone con avvisi di contratto",
                      )}
                    </button>
                  )}
                </div>
                <div className="col-totali">
                  <BloccoTotali
                    assegnati={gruppo.totali.minuti_assegnati}
                    previsti={gruppo.totali.minuti_previsti}
                    delta={gruppo.totali.delta_minuti}
                    unita={unita}
                  />
                </div>
              </div>

              {aperto &&
                reparti
                  .filter(r => r.persone.length > 0)
                  .map(({ reparto, persone }) => (
                    <React.Fragment key={`${chiave}-${reparto.id ?? "nessuno"}`}>
                      {reparti.length > 1 && (
                        <div className="riga reparto">
                          <div className="col-nome">
                            <span className="nome-reparto">{reparto.nome}</span>
                            <span className="conta-persone">
                              {plurale(reparto.totali.persone, "persona", "persone")}
                            </span>
                          </div>
                          <div className="centro-gruppo" />
                          <div className="col-totali">
                            <BloccoTotali
                              assegnati={reparto.totali.minuti_assegnati}
                              previsti={reparto.totali.minuti_previsti}
                              delta={reparto.totali.delta_minuti}
                              unita={unita}
                            />
                          </div>
                        </div>
                      )}
                      {persone.map(persona => (
                        <RigaPersona
                          key={persona.id}
                          persona={persona}
                          oggi={oggi}
                          unita={unita}
                          puoCreare={puoCreare}
                          puoCrearePassato={puoCrearePassato}
                          pulita={pulita}
                          onApriAssegnazione={onApriAssegnazione}
                          onApriTurno={onApriTurno}
                        />
                      ))}
                    </React.Fragment>
                  ))}
            </React.Fragment>
          ))}
      </div>
    </Griglia>
  );
};
